import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, getAdminApiUrl } from '../lib/supabase';
import Logo from './Logo';

interface AccountDeletionRequest {
  id: string;
  email: string;
  name: string;
  reason: string | null;
  user_id: string | null;
  status: string;
  submitted_at: string;
  processed_at: string | null;
  processed_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const AccountDeletionRequests: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<AccountDeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | 'all' | null>(null);
  const [updateLoading, setUpdateLoading] = useState<string | null>(null);
  const [noteEditForId, setNoteEditForId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const itemsPerPage = 50;

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No active session');
      }
      const res = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'listAccountDeletionRequests' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load account deletion requests');
      }
      const payload = await res.json();
      setRequests(payload.requests as AccountDeletionRequest[]);
    } catch (e: any) {
      setError(e.message || 'Failed to load account deletion requests');
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId: string, newStatus: string, notes?: string) => {
    try {
      setUpdateLoading(requestId);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No active session');
      }

      const res = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'updateAccountDeletionRequest',
          requestId,
          status: newStatus,
          notes
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update request');
      }

      await fetchRequests();
      setNoteEditForId(null);
      setNoteText("");
    } catch (e: any) {
      alert(e.message || 'Failed to update request');
    } finally {
      setUpdateLoading(null);
    }
  };

  const filteredRequests = requests.filter(r => {
    const matchesSearch = !search.trim() || (() => {
      const hay = [
        r.email,
        r.name,
        r.reason || '',
        r.status || '',
      ].join(' ').toLowerCase();
      return hay.includes(search.toLowerCase());
    })();

    const matchesStatus = !selectedStatusFilter || selectedStatusFilter === 'all' || r.status === selectedStatusFilter;

    return matchesSearch && matchesStatus;
  });

  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const paginatedRequests = filteredRequests.slice(startIdx, endIdx);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  const statusCounts = requests.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const getStatusBadgeColor = (status: string) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Logo className="h-8 w-8" />
              <span className="ml-2 text-xl font-bold text-gray-800">DealOnce Admin</span>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Account Deletion Requests</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage user account deletion requests
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Status Filter Tiles */}
        <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <button
            onClick={() => setSelectedStatusFilter('all')}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedStatusFilter === 'all' || selectedStatusFilter === null
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="text-2xl font-bold text-gray-900">{requests.length}</div>
            <div className="text-sm text-gray-600">All Requests</div>
          </button>
          <button
            onClick={() => setSelectedStatusFilter('pending')}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedStatusFilter === 'pending'
                ? 'border-yellow-500 bg-yellow-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="text-2xl font-bold text-yellow-800">{statusCounts['pending'] || 0}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </button>
          <button
            onClick={() => setSelectedStatusFilter('processing')}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedStatusFilter === 'processing'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="text-2xl font-bold text-blue-800">{statusCounts['processing'] || 0}</div>
            <div className="text-sm text-gray-600">Processing</div>
          </button>
          <button
            onClick={() => setSelectedStatusFilter('completed')}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedStatusFilter === 'completed'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="text-2xl font-bold text-green-800">{statusCounts['completed'] || 0}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </button>
          <button
            onClick={() => setSelectedStatusFilter('cancelled')}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedStatusFilter === 'cancelled'
                ? 'border-red-500 bg-red-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="text-2xl font-bold text-red-800">{statusCounts['cancelled'] || 0}</div>
            <div className="text-sm text-gray-600">Rejected</div>
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by email, name, reason, or status..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* Requests Table */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRequests.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      No account deletion requests found
                    </td>
                  </tr>
                ) : (
                  paginatedRequests.map((request) => (
                    <React.Fragment key={request.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{request.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{request.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(request.status)}`}>
                            {request.status === 'completed' ? 'Approved' : request.status === 'cancelled' ? 'Rejected' : request.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(request.submitted_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => setExpandedId(expandedId === request.id ? null : request.id)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            {expandedId === request.id ? 'Hide Details' : 'View Details'}
                          </button>
                        </td>
                      </tr>
                      {expandedId === request.id && (
                        <tr>
                          <td colSpan={5} className="px-6 py-4 bg-gray-50">
                            <div className="space-y-4">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Request Details</h4>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <p className="text-xs text-gray-500">User ID</p>
                                    <p className="text-sm text-gray-900">{request.user_id || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Processed At</p>
                                    <p className="text-sm text-gray-900">
                                      {request.processed_at ? new Date(request.processed_at).toLocaleString() : 'Not processed'}
                                    </p>
                                  </div>
                                  <div className="col-span-2">
                                    <p className="text-xs text-gray-500">Reason</p>
                                    <p className="text-sm text-gray-900">{request.reason || 'No reason provided'}</p>
                                  </div>
                                  {request.notes && (
                                    <div className="col-span-2">
                                      <p className="text-xs text-gray-500">Admin Notes</p>
                                      <p className="text-sm text-gray-900">{request.notes}</p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Action Buttons */}
                              {request.status === 'pending' && (
                                <div className="flex gap-2 mt-4">
                                  <button
                                    onClick={() => {
                                      setNoteEditForId(request.id);
                                      setNoteText("");
                                    }}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                  >
                                    Add Note & Update Status
                                  </button>
                                  <button
                                    onClick={() => updateRequestStatus(request.id, 'completed')}
                                    disabled={updateLoading === request.id}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                                  >
                                    {updateLoading === request.id ? 'Processing...' : 'Approve'}
                                  </button>
                                  <button
                                    onClick={() => updateRequestStatus(request.id, 'cancelled')}
                                    disabled={updateLoading === request.id}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                  >
                                    {updateLoading === request.id ? 'Processing...' : 'Reject'}
                                  </button>
                                </div>
                              )}

                              {/* Note Editor */}
                              {noteEditForId === request.id && (
                                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Add Admin Note
                                  </label>
                                  <textarea
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                    rows={3}
                                    placeholder="Enter notes about this request..."
                                  />
                                  <div className="flex gap-2 mt-2">
                                    <button
                                      onClick={() => updateRequestStatus(request.id, 'completed', noteText)}
                                      disabled={updateLoading === request.id}
                                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                                    >
                                      Approve with Note
                                    </button>
                                    <button
                                      onClick={() => updateRequestStatus(request.id, 'cancelled', noteText)}
                                      disabled={updateLoading === request.id}
                                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                    >
                                      Reject with Note
                                    </button>
                                    <button
                                      onClick={() => updateRequestStatus(request.id, 'processing', noteText)}
                                      disabled={updateLoading === request.id}
                                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                      Mark as Processing
                                    </button>
                                    <button
                                      onClick={() => {
                                        setNoteEditForId(null);
                                        setNoteText("");
                                      }}
                                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Showing {startIdx + 1} to {Math.min(endIdx, filteredRequests.length)} of {filteredRequests.length} requests
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-4 py-2 border border-gray-300 rounded-lg bg-white">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountDeletionRequests;
